/**
 * "Data access" for an Azure Storage account signed in with Microsoft Entra ID (RBAC fix 2026-09-25): the identity's
 * own data roles (default), or — opt-in, the Azure Portal's "Switch to access key" — the account key this identity
 * reads through Azure Resource Manager `listKeys` at every connect. The optional resource ID names the account when
 * the identity cannot look it up by name. Shown only for Entra auth in "Storage account" mode. Key access is public-cloud
 * only and bound to the account's own endpoints (review 2026-09-25; `electron/azure-storage/data-access/endpointBinding.ts`).
 */
import { Input } from '@/components/ui/input';
import type { ConnectionFormValues } from './connectionFormSchema';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { SELECT_CLASS, type ConnectionFormApi } from '../connection-dialog/fields/fieldControls';
import { resourceIdOriginOnEdit } from '../connection-dialog/state/provenance';

const ENTRA_AUTH: ReadonlySet<string> = new Set(['Microsoft Entra ID (Interactive Browser)', 'Azure CLI', 'Service Principal']);

export function storageShowsDataAccess(values: Partial<ConnectionFormValues>, accountMode: boolean): boolean {
  return accountMode && ENTRA_AUTH.has(String(values.authentication ?? ''));
}

export function AzureStorageDataAccessFields({ form, values, onModeChange }: { form: ConnectionFormApi; values: Partial<ConnectionFormValues>; onModeChange: (mode: 'entra' | 'account-key-via-arm') => void }) {
  const keyMode = values.azureStorageDataAccess === 'account-key-via-arm';
  return (
    <>
      <FormRow row="azureStorageDataAccess" label="Data access" htmlFor="storage-data-access-select" testId="storage-data-access">
        <select
          id="storage-data-access-select"
          className={SELECT_CLASS}
          value={keyMode ? 'account-key-via-arm' : 'entra'}
          onChange={(event) => {
            const mode = event.target.value === 'account-key-via-arm' ? 'account-key-via-arm' : 'entra';
            form.setFieldValue('azureStorageDataAccess', mode === 'entra' ? undefined : mode);
            onModeChange(mode);
          }}
          data-testid="storage-data-access-select"
        >
          <option value="entra">Microsoft Entra data roles (RBAC)</option>
          <option value="account-key-via-arm">Account key, read by this identity through Azure Resource Manager</option>
        </select>
        {/* Round B U39: one sentence under the field; the rest (roles, RBAC bypass, read-only, public cloud) behind Details. */}
        <div className="mt-1 text-xs text-muted-foreground" data-testid="storage-data-access-hint">
          {keyMode ? (
            <>
              <p>Needs permission to read the account keys; the key is read at every connect and never saved.</p>
              <details className="mt-1">
                <summary className="cursor-pointer select-none text-foreground/80 hover:text-foreground">Details</summary>
                <p className="mt-1">
                  The identity needs listKeys, e.g. Storage Account Key Operator Service Role or Reader and Data Access. Keys give
                  full data access and bypass RBAC. A read-only connection stays read-only. Public Azure only: the key is read
                  through management.azure.com and used only with the account&apos;s own *.core.windows.net endpoints — an
                  endpoint override (More options) that differs from them is refused.
                </p>
              </details>
            </>
          ) : (
            <p>Listing works with Reader; reading blobs, entities and messages needs a Storage Blob / Table / Queue Data Reader (or Contributor) role.</p>
          )}
        </div>
      </FormRow>
      {keyMode && (
        <FormRow row="azureResourceId" label="Storage account resource ID" htmlFor="storage-resource-id" note="optional">
          <Input
            id="storage-resource-id"
            className="font-mono text-xs"
            placeholder="/subscriptions/…/storageAccounts/<account>"
            title="/subscriptions/<subscription>/resourceGroups/<group>/providers/Microsoft.Storage/storageAccounts/<account>"
            value={values.azureResourceId ?? ''}
            onChange={(event) => {
              const value = event.target.value.trim() || undefined;
              form.setFieldValue('azureResourceId', value);
              // A typed id — a picker id edited by hand included — is the user's (C3.1); never cleared automatically.
              form.setFieldValue('azureResourceIdOrigin', resourceIdOriginOnEdit(value), { dontUpdateMeta: true });
            }}
            data-testid="storage-resource-id-input"
          />
          <p className="mt-1 text-xs text-muted-foreground">Looked up by account name when empty — that needs Reader on the account&apos;s subscription or resource group. A resource ID that names another account is refused.</p>
        </FormRow>
      )}
    </>
  );
}
